import React from 'react';
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import PropTypes from 'prop-types';

import { GetJson, PostJson, DeleteJson, PutJson } from '@awp/helpers/fetchReact';
import { REST_API, STATE_STATUS } from '@awp/constants';
import { ToastContainer, toast } from 'react-toastify';
import AwpToastContent from '@components/Common/AwpToastContent';

const initialState = {
  userPresets: [],
  userPreset: null,
  saveUserPresetStatus: STATE_STATUS.IDLE,
  saveUserPresetError: null,
  deleteUserPresetStatus: STATE_STATUS.IDLE,
  deleteUserPresetError: null,
};

export const selectAllUserPresets = (state) => state.userPreset.userPresets;
export const selectedUserPreset = (state) => state.userPreset.userPreset;
export const getSaveUserPresetError = (state) => state.userPreset.saveUserPresetError;
export const getDeleteUserPresetError = (state) => state.userPreset.deleteUserPresetError;
export const getSaveUserPresetStatus = (state) => state.userPreset.saveUserPresetStatus;
/**
 * Thunks
 */
export const fetchUserPresets = createAsyncThunk('userPreset/fetchUserPresets', async () => {
  let url = REST_API.MY_PRESETS;
  const response = await GetJson(url);
  return await response.json();
});
export const deleteUserPreset = createAsyncThunk('userPreset/deleteUserPreset', async (payload, { getState }) => {
  const state = getState();
  const id = state.userPreset.userPreset.Id;
  const url = REST_API.MY_PRESETS + '/' + id;

  const resp = await DeleteJson(url, state.userPreset.userPreset);
  if (resp.ok) {
    return { response: await resp.json() };
  }
  return { response: null, error: await resp.json() };
});
export const saveUserPreset = createAsyncThunk('userPreset/saveUserPreset', async (payload, { getState }) => {
  const state = getState();
  const url = REST_API.MY_PRESETS;
  const resp = await PutJson(url, state.userPreset.userPreset);
  if (resp.ok) return { response: await resp.json() };
  return { response: null, error: await resp.json() };
});
export const userPresetSlice = createSlice({
  name: 'userPreset',
  initialState,
  reducers: {
    setUserPreset(state, action) {
      return { ...state, userPreset: action.payload };
    },
    enableUserPreset(state, action) {
      const _enableUserPreset = action.payload;
      let _userPresets = [...state.userPresets];
      let _newUserPresets = [];
      _userPresets.map((up) => {
        let _up = { ...up };
        if (_up.Id === _enableUserPreset.Id) {
          _newUserPresets.push({ ..._enableUserPreset });
        } else {
          _newUserPresets.push(_up);
        }
      });
      state.userPresets = [..._newUserPresets];
    },
    setSaveUserPresetStatus(state, action) {
      return { ...state, saveUserPresetStatus: action.payload };
    },
    setSaveUserPresetError(state, action) {
      return { ...state, saveUserPresetError: action.payload };
    },
    setDeleteUserPresetStatus(state, action) {
      return { ...state, deleteUserPresetStatus: action.payload };
    },
    setDeleteUserPresetError(state, action) {
      return { ...state, deleteUserPresetError: action.payload };
    },
  },
  extraReducers: {
    [fetchUserPresets.pending.toString()]: (state) => {
      state.status = STATE_STATUS.LOADING;
    },
    [fetchUserPresets.fulfilled.toString()]: (state, { payload }) => {
      state.status = STATE_STATUS.SUCCESS;
      state.userPresets = payload;
    },
    [fetchUserPresets.rejected.toString()]: (state) => {
      state.status = STATE_STATUS.FAILED;
    },
    [saveUserPreset.pending]: (state) => {
      state.saveUserPresetStatus = STATE_STATUS.LOADING;
    },
    [saveUserPreset.fulfilled]: (state, action) => {
      const { response, error } = action.payload;
      if (!response) {
        state.saveUserPresetStatus = STATE_STATUS.FAILED;
        if (error) {
          state.saveUserPresetError = { ...error };
          if (error.entity) {
            toast(<AwpToastContent title={'Could not save User Preset !'} msgList={error.entity} />, {
              autoClose: false,
            });
            return;
          }
        }
        return;
      }
      state.saveUserPresetStatus = STATE_STATUS.SUCCESS;
      let savedUserPreset = { ...response };
      let _userPresets = [...state.userPresets];
      let _newUserPresets = [];
      let isNew = true;
      _userPresets.map((up) => {
        let _up = { ...up };
        if (_up.Id === savedUserPreset.Id) {
          isNew = false;
          _newUserPresets.push(savedUserPreset);
        } else {
          _newUserPresets.push(_up);
        }
      });
      if (isNew) _newUserPresets.push(savedUserPreset);
      state.userPresets = [..._newUserPresets];
      /////////////////
      //cleanData
      state.userPreset = null;
      state.saveUserPresetError = null;
      toast.success('User Preset successfully saved!');
    },
    [saveUserPreset.rejected]: (state, action) => {
      state.saveUserPresetStatus = STATE_STATUS.FAILED;
    },
    [deleteUserPreset.pending]: (state) => {
      state.deleteUserPresetStatus = STATE_STATUS.LOADING;
    },
    [deleteUserPreset.fulfilled]: (state, action) => {
      const { response, error } = action.payload;
      if (!response) {
        state.deleteUserPresetStatus = STATE_STATUS.FAILED;

        if (error) {
          state.deleteUserPresetError = { ...error };
          if (error.entity) {
            toast(<AwpToastContent title={'Could not delete User Preset !'} msgList={error.entity} />, {
              autoClose: false,
            });
            return;
          }
          toast.error('Could not delete User Preset ! ', {
            autoClose: false,
          });
        }
        return;
      }

      state.deleteUserPresetStatus = STATE_STATUS.SUCCESS;
      let deletedUserPreset = { ...response };
      let _userPresets = [...state.userPresets];
      let _newUserPresets = [];
      _userPresets.map((up) => {
        let _up = { ...up };
        if (_up.Id !== deletedUserPreset.Id) {
          _newUserPresets.push(_up);
        }
      });
      state.userPresets = [..._newUserPresets];

      /////////////////
      //cleanData
      state.userPreset = null;
      state.deleteUserPresetError = null;
      toast.success('User Preset successfully deleted!');
    },
    [deleteUserPreset.rejected]: (state, action) => {
      state.deleteUserPresetStatus = STATE_STATUS.FAILED;
    },
  },
});

export const {
  setUserPreset,
  setSaveUserPresetStatus,
  setSaveUserPresetError,
  setDeleteUserPresetStatus,
  setDeleteUserPresetError,
  enableUserPreset,
} = userPresetSlice.actions;
